import type { ReactElement } from 'react';
import { Badge } from '@/components/atoms/badge';
import { Icon } from '@/components/atoms/icon';
import { cn } from '@/lib/cn';
import type { Piece } from '@/lib/types';

const COMPOSER_FALLBACK = 'Traditional';

export type SearchResultRowProps = {
  piece: Piece;
  selected: boolean;
  onSelect: () => void;
};

export function SearchResultRow({ piece, selected, onSelect }: SearchResultRowProps): ReactElement {
  const composer = piece.composerName ?? COMPOSER_FALLBACK;
  const settingsLabel = formatSettingsCount(piece.arrangementCount);

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={selected ? 'true' : undefined}
      className={cn(
        'flex w-full items-center gap-3 border-b border-l-4 border-[var(--rule)] py-3 pr-4 pl-5 text-left transition-colors',
        selected
          ? 'border-l-[var(--brand)] bg-[var(--bg-soft)]'
          : 'border-l-transparent hover:bg-[var(--bg-soft)]',
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="font-display text-base font-semibold text-[var(--ink)]">
            {piece.prettyName}
          </span>
          {piece.inRepertoire === true && (
            <Badge tone="ok" size="sm">In repertoire</Badge>
          )}
        </div>
        <span className="text-xs text-[var(--ink-3)]">{composer}</span>
        {settingsLabel !== null && (
          <span className="font-mono text-[11px] uppercase tracking-wider text-[var(--ink-3)]">
            {settingsLabel}
          </span>
        )}
      </div>
      {selected && (
        <Icon name="chev_r" size={16} className="shrink-0 text-[var(--ink-3)]" />
      )}
    </button>
  );
}

function formatSettingsCount(count: number | undefined): string | null {
  if (count === undefined || count === 0) return null;
  if (count === 1) return '1 setting';
  return `${count} settings`;
}
